// ExecDrill (R115 C6 — Jesse: "Executive Summary cards clickable → drill-down
// popup, e.g. click Payment flags → the reservations with payment flags"):
// the list behind an Executive Summary number. ONE sheet shared by every
// card: GET /api/exec/drill/:card answers rows built by the SAME selector the
// card's count comes from (lib/execDrill.js), so the rows always add up to
// the card.
//
// Mounted on document.body (the WoQuickView pattern): the Analytics hub
// re-mounts the Executive Summary on every hub render and on the 15-min
// auto-refresh, so a sheet inside the page would be destroyed mid-read. It
// holds no unsaved state. Bottom sheet on a phone, a centered 600px modal
// from 640px up. role="dialog", Esc / backdrop / ✕ close, focus lands on ✕,
// Tab wraps, focus returns to the card on close.
//
// Each row: ONE primary inline action ("Open" — writes the destination
// page's one-shot sessionStorage keys, sets hub_tab only for hubs, then
// app:navigate) + the shared ⋯ Kebab for the secondary links. Footer:
// "Open <page>" for the list-level destination. Read-only — nothing here
// writes anything. Money cards (pace, profit margin) render only for admin /
// director (the server enforces the same gate). Every value is escaped.
import { Formatters } from '../lib/formatters';
import { LoadingSpinner } from './LoadingSpinner';
import { kebabHtml, bindKebabs, closeKebab, KebabItem } from './Kebab';
// R115 fix: a row's sessionStorage writes (an Arrivals Day link goes through
// the Day view's own hand-off — lib/arrivalsDayView arrivalsHandoff).
import { drillLinkWrites } from '../../../../lib/execDrillLink.mjs';
import { STORE_KEYS as RT_KEYS } from '../../../../lib/arrivalsDayView.mjs';

const ROOT_ID = 'xd-root';
const MONEY = new Set(['adr_occ_revpar', 'profit_margin']);
const esc = (x: any): string => Formatters.escapeHtml(String(x == null ? '' : x));

export interface ExecLink { page: string; hub_tab?: string; keys?: Record<string, string>; label?: string }
export interface ExecDrillOpts {
  /** The card's title (shown until the payload's own label lands). */
  label?: string;
  /** The number the card shows ("3", "4.75/5", "+100") — the sheet's headline until the payload lands. */
  value?: string;
  /** The card's basis line. */
  basis?: string;
  /** The page id hosting the summary (a link to this same hub switches tabs instead of navigating). */
  hostPage?: string;
  /** Focus goes back here on close. */
  returnFocus?: HTMLElement | null;
}

// What the headline count means, per card.
const NOUN: Record<string, string> = {
  review_score: 'reviews in the last 30 days',
  bookings_24h: 'booked today (business time)',
  mtr_open: 'MTR homes vacant now',
  adr_occ_revpar: 'active listings, softest calendar first',
  blocks_no_note: 'blocked ranges with no note',
  profit_margin: 'listings with revenue last month',
  owner_nps: 'owner responses · trailing 365 days',
  properties_added_ytd: 'listings first booked this year',
  listings_suspended: 'suspended in Hostaway',
  tasks_past_due: 'open tasks past their due date',
  at_risk_arrivals: 'arrivals inside 48h, checklist not verified',
  payment_flags: 'open payment flags',
  inspections_unassigned_7d: 'checkouts in the next 7 days with no inspector',
  cleans_unassigned_7d: 'turns in the next 7 days with no cleaner',
};
const EMPTY: Record<string, string> = {
  payment_flags: 'No open payment flags. 🎉',
  tasks_past_due: 'Nothing past due. 🎉',
  at_risk_arrivals: 'Every arrival inside 48h is verified. 🎉',
  inspections_unassigned_7d: 'Every checkout this week has an inspector. 🎉',
  cleans_unassigned_7d: 'Every turn this week has a cleaner. 🎉',
  blocks_no_note: 'Every blocked night has a note. 🎉',
  mtr_open: 'No MTR home is vacant today. 🎉',
  listings_suspended: 'Nothing suspended and no channel gaps in the snapshot.',
  bookings_24h: 'No bookings yet today.',
  review_score: 'No published reviews in the last 30 days.',
  owner_nps: 'No owner survey responses in the last year — send links from owner profiles.',
  properties_added_ytd: 'No listing had its first booking this year yet.',
  profit_margin: 'No listing earned revenue last month.',
  adr_occ_revpar: 'No active listings.',
};

// Cards whose number is a score, not a count — the sheet headlines the score.
const SCORE: Record<string, (v: any) => string> = {
  review_score: (v) => `${v}/5`,
  owner_nps: (v) => `${Number(v) > 0 ? '+' : ''}${v}`,
  profit_margin: (v) => `${v}%`,
};

let current: ExecDrill | null = null;

export class ExecDrill {
  static isOpen(): boolean { return !!(current && current.root.isConnected); }
  static open(card: string, opts: ExecDrillOpts = {}): ExecDrill {
    if (current) current.close();
    const v = new ExecDrill(card, opts);
    current = v;
    v.mount();
    return v;
  }
  static close(): void { if (current) current.close(); }
  private static role(): string { try { return sessionStorage.getItem('userRole') || ''; } catch { return ''; } }

  private root: HTMLElement;
  private card: string;
  private opts: ExecDrillOpts;
  private data: any = null;
  private status: 'loading' | 'ok' | 'forbidden' | 'error' = 'loading';
  private err = '';

  private constructor(card: string, opts: ExecDrillOpts) {
    this.card = String(card);
    this.opts = opts;
    this.root = document.createElement('div');
  }

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
    if (e.key !== 'Tab') return;
    const f = Array.from(this.root.querySelectorAll<HTMLElement>('button, [href], input, select, textarea'))
      .filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  private mount(): void {
    ExecDrill.injectStyle();
    this.root.id = ROOT_ID;
    this.root.className = 'xd';
    document.body.appendChild(this.root);
    document.body.classList.add('xd-modal-open');
    document.addEventListener('keydown', this.onKey);
    this.draw();
    (this.root.querySelector('.xd-x') as HTMLElement | null)?.focus();
    void this.load();
  }

  close(): void {
    closeKebab();
    this.root.remove();
    document.body.classList.remove('xd-modal-open');
    document.removeEventListener('keydown', this.onKey);
    if (current === this) current = null;
    const back = this.opts.returnFocus;
    if (back && back.isConnected) { try { back.focus(); } catch { /* detached */ } }
  }

  private async load(): Promise<void> {
    if (MONEY.has(this.card) && !['admin', 'director'].includes(ExecDrill.role())) {
      this.status = 'forbidden'; this.draw(); (this.root.querySelector('.xd-x') as HTMLElement | null)?.focus(); return;
    }
    if (this.status !== 'loading') { this.status = 'loading'; this.err = ''; this.draw(); }   // a retry
    try {
      const r = await fetch(`/api/exec/drill/${encodeURIComponent(this.card)}`);
      if (r.status === 403) { this.status = 'forbidden'; this.draw(); return; }
      const j = await r.json().catch(() => null);
      if (!r.ok || !j) { this.status = 'error'; this.err = (j && j.error) || `HTTP ${r.status}`; this.draw(); return; }
      this.data = j; this.status = 'ok';
    } catch {
      this.status = 'error'; this.err = 'network error';
    }
    if (current !== this) return;
    // keep focus inside the sheet across the redraw (it re-renders whole)
    const ae = document.activeElement;
    const refocus = !ae || ae === document.body || this.root.contains(ae);
    this.draw();
    if (refocus) (this.root.querySelector('.xd-x') as HTMLElement | null)?.focus();
  }

  // ── navigation ─────────────────────────────────────────────────────────
  private go(link: ExecLink | null | undefined): void {
    if (!link || !link.page) return;
    // Focus keys FIRST (docs/CSS_STANDARD.md), then hub_tab — only for hubs.
    // An Arrivals Day link flips a stored Check-outs tab to Check-ins and pins
    // the day only inside the Day view's window (past days included), so the
    // stay is on screen when the page opens (lib/execDrillLink, tested).
    try {
      const today = String((this.data && this.data.as_of) || '');
      const w = drillLinkWrites(link, today, sessionStorage.getItem(RT_KEYS.mode));
      for (const [k, v] of Object.entries(w.set)) sessionStorage.setItem(k, v);
      for (const k of w.remove) sessionStorage.removeItem(k);
    } catch { /* private mode */ }
    const host = this.opts.hostPage || '';
    this.close();
    if (host && link.page === host) {
      // Same hub already on screen (e.g. Analytics → Booked · Projected ·
      // Actual): App.navigate ignores the current page, so switch the tab.
      const btn = link.hub_tab ? document.querySelector<HTMLElement>(`#hub-tabs [data-hub-tab="${link.hub_tab.replace(/"/g, '')}"]`) : null;
      if (btn) btn.click();
      return;
    }
    if (link.hub_tab) { try { sessionStorage.setItem('hub_tab', link.hub_tab); } catch { /* private mode */ } }
    document.dispatchEvent(new CustomEvent('app:navigate', { detail: link.page }));
  }

  // ── render ─────────────────────────────────────────────────────────────
  private fmtDay(s: any): string {
    const v = String(s || '');
    if (!/^\d{4}-\d{2}-\d{2}/.test(v)) return '';
    const d = new Date(v.slice(0, 10) + 'T00:00:00Z');
    const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getUTCMonth()];
    const y = d.getUTCFullYear();
    return `${mon} ${d.getUTCDate()}${y !== new Date().getFullYear() ? `, ${y}` : ''}`;
  }
  private money(n: any): string {
    const v = Number(n);
    return Number.isFinite(v) ? `$${Math.round(v).toLocaleString('en-US')}` : '—';
  }

  private headline(): string {
    const j = this.data;
    if (this.status !== 'ok' || !j) {
      return this.opts.value ? `<div class="xd-big num">${esc(this.opts.value)}</div>` : '';
    }
    if (j.unavailable) return `<div class="xd-big num">—</div>`;
    let extra = '';
    if (this.card === 'bookings_24h' && j.prev != null) extra = `yesterday <strong class="num">${esc(j.prev)}</strong>`;
    else if (this.card === 'mtr_open' && j.of != null) extra = `of <strong class="num">${esc(j.of)}</strong> in the MTR set`;
    else if (this.card === 'listings_suspended' && j.gap_rows != null) extra = `<strong class="num">${esc(j.gap_rows)}</strong> with a channel gap`;
    else if (this.card === 'adr_occ_revpar' && j.value && j.value.next30) {
      const n = j.value.next30;
      extra = `next 30: <strong class="num">${esc(n.occ ?? '—')}%</strong> occ · <strong class="num">${this.money(n.adr)}</strong> ADR · <strong class="num">${this.money(n.revpar)}</strong> RevPAR`;
    } else if (this.card === 'profit_margin') {
      extra = `${esc(j.month || '')}: revenue <strong class="num">${this.money(j.revenue_total)}</strong> · expenses <strong class="num">${j.expense_rows_in_month ? this.money(j.expense_total) : 'none recorded'}</strong>`;
    }
    const count = j.count == null ? '—' : String(j.count);
    // Score cards: the headline is the card's own number (4.73/5, +100, 32%),
    // never the row count — a big "79" under "Profit margin" reads as 79%.
    const score = SCORE[this.card];
    if (score) {
      const big = j.value == null ? '—' : score(j.value);
      return `<div class="xd-big num">${esc(big)}</div><div class="xd-noun">${esc(`${count} ${NOUN[this.card] || ''}`)}${extra ? `<span class="xd-extra">${extra}</span>` : ''}</div>`;
    }
    return `<div class="xd-big num">${esc(count)}</div><div class="xd-noun">${esc(NOUN[this.card] || '')}${extra ? `<span class="xd-extra">${extra}</span>` : ''}</div>`;
  }

  private rowHtml(r: any, i: number): string {
    const tone = r.tone === 'bad' ? 'bad' : r.tone === 'warn' ? 'warn' : r.tone === 'good' ? 'good' : '';
    const date = this.fmtDay(r.date);
    const to = r.to && r.to !== r.date ? this.fmtDay(r.to) : '';
    const meta = [r.who, r.sub].filter((x: any) => x != null && String(x).trim() !== '').map(esc).join(' · ');
    const items: KebabItem[] = [];
    (Array.isArray(r.links) ? r.links : []).forEach((l: any, k: number) => {
      items.push({ label: l.label || 'Open', icon: '↗', attrs: { 'data-xd-go': `${i}:${k}` } });
    });
    // (the list-level page is the sheet's footer button — not repeated per row)
    items.push({ label: 'Copy details', icon: '⧉', attrs: { 'data-xd-copy': String(i) } });
    return `
      <li class="xd-row">
        <div class="xd-main">
          <div class="xd-l1"><span class="xd-name">${esc(r.listing_name || r.key || '—')}</span>${date ? `<span class="xd-date num">${esc(date)}${to ? ` → ${esc(to)}` : ''}</span>` : ''}</div>
          <div class="xd-l2"><span class="xd-tag ${tone}">${esc(r.label || '')}</span>${meta ? `<span class="xd-meta">${meta}</span>` : ''}</div>
        </div>
        <div class="xd-acts">
          <button type="button" class="btn xd-go" data-xd-open="${i}" aria-label="Open ${esc(r.listing_name || '')}">Open</button>
          ${kebabHtml(items, { tip: 'More', cls: 'xd-kb' })}
        </div>
      </li>`;
  }

  private bodyHtml(): string {
    if (this.status === 'loading') return `<div class="xd-pad">${LoadingSpinner.render()}</div>`;
    if (this.status === 'forbidden') return `<div class="xd-state">🔒 Limited to Admin and Director roles.</div>`;
    if (this.status === 'error') return `<div class="xd-state">Couldn't load this list (${esc(this.err)}).<div><button type="button" class="btn btn-secondary btn-sm xd-retry" data-xd-retry>Try again</button></div></div>`;
    const j = this.data || {};
    const notes = (Array.isArray(j.notes) ? j.notes : []).map((n: string) => `<div class="xd-note">${esc(n)}</div>`).join('');
    if (j.unavailable) {
      return `<div class="xd-state">⚠️ The data behind this card is unreadable right now — this is <strong>not</strong> zero. Try again in a moment.<div class="xd-fine">${esc(j.error || '')}</div><div><button type="button" class="btn btn-secondary btn-sm" data-xd-retry>Try again</button></div></div>`;
    }
    const rows: any[] = Array.isArray(j.rows) ? j.rows : [];
    const list = rows.length
      ? `<ul class="xd-list">${rows.map((r, i) => this.rowHtml(r, i)).join('')}</ul>`
      : `<div class="xd-state">${esc(EMPTY[this.card] || 'Nothing to show.')}</div>`;
    const trunc = j.truncated ? `<div class="xd-fine">Showing the first ${esc(rows.length)} of ${esc(j.total_rows)} — open ${esc(j.page_label || 'the page')} for the rest.</div>` : '';
    const exp = this.card === 'profit_margin' && Array.isArray(j.expense_lines) && j.expense_lines.length
      ? `<div class="xd-sec">Expense lines · ${esc(j.month)}</div><ul class="xd-list xd-exp">${j.expense_lines.map((e: any) => `<li class="xd-exprow"><span class="xd-name">${esc(e.concept || '—')}</span><span class="xd-meta">${esc(e.listing_name || '')} · ${esc(this.fmtDay(e.date))}</span><span class="xd-amt num">${this.money(e.amount)}</span></li>`).join('')}</ul>`
      : '';
    return `${notes}${list}${trunc}${exp}`;
  }

  private draw(): void {
    closeKebab();
    const j = this.status === 'ok' ? this.data || {} : {};
    const label = j.label || this.opts.label || 'Executive Summary';
    const basis = j.basis || this.opts.basis || '';
    const stamp = j.as_of ? `as of ${this.fmtDay(j.as_of)}` : '';
    const foot = j.link_default && j.page_label
      ? `<div class="xd-foot"><button type="button" class="btn btn-primary xd-foot-btn" data-xd-default>Open ${esc(j.page_label)} →</button></div>`
      : '';
    this.root.innerHTML = `
      <div class="xd-backdrop" data-xd-close></div>
      <div class="xd-card" role="dialog" aria-modal="true" aria-labelledby="xd-title">
        <div class="xd-head">
          <div class="xd-titles">
            <div class="xd-title" id="xd-title">${esc(label)}</div>
            <div class="xd-headline">${this.headline()}</div>
            ${basis ? `<div class="xd-basis">${esc(basis)}${stamp ? ` · ${esc(stamp)}` : ''}</div>` : ''}
          </div>
          <button type="button" class="xd-x" data-xd-close aria-label="Close">✕</button>
        </div>
        <div class="xd-body">${this.bodyHtml()}</div>
        ${foot}
      </div>`;
    this.bind();
  }

  private bind(): void {
    this.root.querySelectorAll<HTMLElement>('[data-xd-close]').forEach(el => el.addEventListener('click', () => this.close()));
    this.root.querySelectorAll<HTMLElement>('[data-xd-retry]').forEach(el => el.addEventListener('click', () => { void this.load(); }));
    const rows: any[] = (this.data && Array.isArray(this.data.rows)) ? this.data.rows : [];
    this.root.querySelectorAll<HTMLElement>('[data-xd-open]').forEach(el => el.addEventListener('click', () => {
      const r = rows[Number(el.dataset.xdOpen)];
      if (r) this.go(r.link);
    }));
    // Kebab items are MOVED into a popover on body when the menu opens, so
    // they get their own listeners (a delegated one on the sheet would miss them).
    this.root.querySelectorAll<HTMLElement>('[data-xd-go]').forEach(el => el.addEventListener('click', () => {
      const [ri, k] = String(el.dataset.xdGo || '').split(':');
      const r = rows[Number(ri)];
      if (!r) return;
      const l = Array.isArray(r.links) ? r.links[Number(k)] : null;
      if (l) this.go(l);
    }));
    this.root.querySelectorAll<HTMLElement>('[data-xd-copy]').forEach(el => el.addEventListener('click', () => {
      const r = rows[Number(el.dataset.xdCopy)];
      if (!r) return;
      const text = [r.listing_name, r.date, r.to && r.to !== r.date ? `→ ${r.to}` : '', r.label, r.who, r.sub].filter(x => x != null && String(x).trim() !== '').join(' · ');
      try { void navigator.clipboard?.writeText(text); } catch { /* clipboard blocked */ }
    }));
    this.root.querySelector<HTMLElement>('[data-xd-default]')?.addEventListener('click', () => this.go(this.data && this.data.link_default));
    bindKebabs(this.root);
  }

  private static injectStyle(): void {
    if (document.getElementById('xd-style')) return;
    const s = document.createElement('style');
    s.id = 'xd-style';
    s.textContent = `
      .xd { position:fixed; inset:0; z-index:9000; display:flex; align-items:flex-end; justify-content:center; }
      @media (min-width:640px) { .xd { align-items:center; padding:16px; } }
      body.xd-modal-open .kb-pop { z-index:9100; }   /* a row's ⋯ menu opens above the sheet */
      .xd-backdrop { position:absolute; inset:0; background:rgba(15,23,42,.5); }
      .xd-card { position:relative; background:var(--card); border:1px solid var(--border); border-radius:var(--radius-lg) var(--radius-lg) 0 0; box-shadow:var(--shadow-pop); width:100%; max-width:600px; max-height:92vh; display:flex; flex-direction:column; box-sizing:border-box; overflow:hidden; }
      @media (min-width:640px) { .xd-card { border-radius:var(--radius-lg); max-height:86vh; } }
      .xd-head { flex:none; display:flex; gap:10px; align-items:flex-start; padding:14px 16px 12px; border-bottom:1px solid var(--border-soft); }
      .xd-titles { flex:1; min-width:0; }
      .xd-title { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:var(--text-muted); }
      .xd-headline { display:flex; align-items:baseline; gap:10px; flex-wrap:wrap; margin-top:4px; }
      .xd-big { font-size:28px; font-weight:700; letter-spacing:-.02em; color:var(--text); line-height:1.05; font-variant-numeric:tabular-nums; }
      .xd-noun { font-size:12.5px; color:var(--text-muted); line-height:1.4; min-width:0; overflow-wrap:anywhere; }
      .xd-extra { display:block; color:var(--text); margin-top:2px; }
      .xd-basis { font-size:11px; color:var(--text-faint); margin-top:6px; line-height:1.45; overflow-wrap:anywhere; }
      .xd-x { flex:none; width:36px; height:36px; border-radius:var(--radius-pill); border:none; background:transparent; color:var(--text-muted); font-size:16px; cursor:pointer; }
      .xd-x:hover { background:var(--bg-subtle); color:var(--text); }
      .xd-x:focus-visible { outline:none; box-shadow:var(--focus-ring); }
      .xd-body { flex:1; min-height:0; overflow:auto; overscroll-behavior:contain; padding:10px 16px 14px; -webkit-overflow-scrolling:touch; }
      .xd-pad { padding:28px 0; text-align:center; }
      .xd-state { padding:28px 8px; text-align:center; font-size:13px; color:var(--text-muted); line-height:1.5; display:flex; flex-direction:column; gap:10px; align-items:center; }
      .xd-note { font-size:12px; line-height:1.45; padding:8px 11px; border-radius:var(--radius-sm); background:var(--warning-soft); color:var(--warning-text); border:1px solid var(--warning-border); margin-bottom:8px; overflow-wrap:anywhere; }
      .xd-fine { font-size:11px; color:var(--text-faint); line-height:1.45; margin-top:8px; }
      .xd-sec { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); margin:14px 0 4px; }
      .xd-list { list-style:none; margin:0; padding:0; }
      .xd-row { display:flex; gap:10px; align-items:center; padding:9px 0; border-bottom:1px solid var(--border-soft); min-height:44px; }
      .xd-row:last-child { border-bottom:none; }
      .xd-main { flex:1; min-width:0; }
      .xd-l1 { display:flex; gap:8px; align-items:baseline; flex-wrap:wrap; }
      .xd-name { font-size:13px; font-weight:600; color:var(--text); overflow-wrap:anywhere; min-width:0; }
      .xd-date { font-size:12px; color:var(--text-muted); white-space:nowrap; }
      .xd-l2 { display:flex; gap:6px; align-items:center; flex-wrap:wrap; margin-top:3px; font-size:12px; color:var(--text-muted); line-height:1.4; }
      .xd-meta { min-width:0; overflow-wrap:anywhere; }
      .xd-tag { display:inline-block; max-width:100%; font-size:10.5px; font-weight:600; padding:1px 8px; border-radius:var(--radius-pill); background:var(--bg-subtle); color:var(--text-muted); line-height:1.6; overflow-wrap:anywhere; }
      .xd-tag.bad { background:var(--danger-soft); color:var(--danger-text); }
      .xd-tag.warn { background:var(--warning-soft); color:var(--warning-text); }
      .xd-tag.good { background:var(--success-soft); color:var(--success-text); }
      .xd-acts { flex:none; display:flex; gap:4px; align-items:center; }
      .xd-go { min-height:36px; padding:0 14px; background:var(--accent-soft); color:var(--accent); border:1px solid var(--accent-border); font-size:12.5px; }
      .xd-go:hover:not(:disabled) { background:var(--accent); color:#fff; border-color:var(--accent); }
      .xd .kb-btn.xd-kb { min-width:36px; height:36px; }
      .xd-exprow { display:flex; gap:8px; align-items:baseline; flex-wrap:wrap; padding:6px 0; border-bottom:1px dashed var(--border-soft); font-size:12px; }
      .xd-exprow .xd-name { font-size:12.5px; font-weight:500; }
      .xd-amt { margin-left:auto; font-weight:600; color:var(--text); }
      .xd-foot { flex:none; display:flex; justify-content:flex-end; gap:8px; padding:10px 16px calc(10px + env(safe-area-inset-bottom, 0px)); border-top:1px solid var(--border-soft); background:var(--card); }
      .xd-foot-btn { min-height:40px; }
      @media (max-width:639px) { .xd-foot-btn { flex:1; justify-content:center; } }
    `;
    document.head.appendChild(s);
  }
}

export function openExecDrill(card: string, opts: ExecDrillOpts = {}): ExecDrill { return ExecDrill.open(card, opts); }
export function closeExecDrill(): void { ExecDrill.close(); }
